import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrenciesService } from '@app/shared/services/currencies/currencies.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DirectService, PromoCode, PromoCodeType } from '../../services/direct.service';

@Component({
  standalone: false,
  templateUrl: './direct-promo-codes-add.component.html',
  styleUrls: ['./direct-promo-codes-add.component.scss'],
})
export class DirectPromoCodesAddComponent implements OnInit {
  promoCode: PromoCode = {
    uuid: null,
    enabled: true,
    codes: [],
    discount_amount: null,
    discount_type: PromoCodeType.Percentage,
    max_discount: null,
    restrictions: {
      min_length_of_stay: 1,
      min_booking_value: null,
      max_uses: null,
      expires_at: null,
      earliest_check_in_at: null,
      latest_check_out_at: null,
      stacks_with_length_of_stay_discounts: true,
    },
    properties: [],
    reservations: [],
  };

  overlappingProperties: any[] = [];

  stage: 'codes' | 'restrictions' | 'properties' = 'codes';
  saving = false;
  currencies$ = this.currenciesService.getCurrencies(true);
  PromoCodeType = PromoCodeType;

  constructor(
    private directService: DirectService,
    private router: Router,
    private route: ActivatedRoute,
    private currenciesService: CurrenciesService,
    private propertiesService: PropertiesService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const uuid = params.get('uuid');

      if (uuid) {
        this.directService.getPromoCode(uuid).subscribe((response) => {
          this.promoCode = response;
        });
      }
    });
  }

  saveCode() {
    const { currency, ...withoutCurrency } = this.promoCode;
    const promoCode = this.promoCode.discount_type === PromoCodeType.Percentage ? withoutCurrency : this.promoCode;
    this.saving = true;

    this.directService.createPromoCode(promoCode).subscribe(
      () => {
        this.saving = false;
        this.router.navigate(['/direct/promotions']);
      },
      (err) => {
        this.saving = false;
      }
    );
  }

  moveTo(stage) {
    if (this.validate(stage)) {
      this.stage = stage;
    }
  }

  validate(stage): boolean {
    if (stage === 'restrictions') {
      return this.promoCode.codes.length > 0 && this.promoCode.discount_amount > 0;
    }

    return true;
  }

  goBack(): void {
    switch (this.stage) {
      case 'restrictions':
        this.stage = 'codes';
        break;
      case 'properties':
        this.stage = 'restrictions';
        break;
    }
  }

  checkPropertyOverlaps() {
    let overlappingProperties = [];

    const promoCodes$: Observable<PromoCode[]> = this.directService.getPromoCodes();
    const propertiesNotMatchingCurrency$: Observable<any> = this.propertiesService
      .getProperties({
        paginate: false,
        transformer: 'simple',
      })
      .pipe(map((res) => res.data.filter((property) => property.currency !== this.promoCode.currency)));

    combineLatest([promoCodes$, propertiesNotMatchingCurrency$]).subscribe(
      ([promoCodes, propertiesNotMatchingCurrency]) => {
        const overlappingCodes = promoCodes.filter(
          (existingPromo) =>
            existingPromo.uuid !== this.promoCode.uuid &&
            existingPromo.codes.some((code) => this.promoCode.codes.includes(code))
        );

        overlappingCodes.forEach((code) => {
          overlappingProperties.push(...code.properties);
        });

        // Add properties from propertiesNotMatchingCurrency$ to overlappingProperties. Dont do this for only fixed promo codes
        this.promoCode.discount_type === PromoCodeType.Fixed
          ? overlappingProperties.push(...propertiesNotMatchingCurrency.map((property) => property.id))
          : null;

        overlappingProperties = [...new Set(overlappingProperties)];

        overlappingProperties = overlappingProperties.map((property) => {
          const reason = overlappingCodes.some((code) => code.properties.includes(property))
            ? 'This property is already included in another promo code with the same code.'
            : 'This property does not match the currency set for the promo code.';

          return {
            id: property,
            reason,
          };
        });

        this.overlappingProperties = overlappingProperties;

        this.stage = 'properties';
      }
    );
  }
}
